// import components
import { Header } from 'components/roster-portal/home/travel-expense-claims/components';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

// import enums
import { BonusTypesEnums } from './EmployeeBonuses.enums';

// import external libraries
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import hooks
import { useRecalculateDutyLinesForEmployeeMutation } from 'components/admin-portal/Procedures/Realization/queries';
import useGetEmployeeBonuses from './hooks/useGetEmployeeBonuses';
import { useGetColumns } from './hooks/useGetColumns';

// import stores
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';

// import types
import { TabType } from 'components/admin-portal/BaseTabsRouting/types';
import { EmployeeBonusesProps } from './EmployeeBonuses.types';

const EmployeeBonuses = ({ page, employeeId }: EmployeeBonusesProps) => {
  const isEmployeePortal = page === 'EMPLOYEE_PORTAL';
  const { t } = useTranslation('common', { keyPrefix: 'employeeBonuses' });
  const { selectedDate, setSelectedDate } = useDateNavigationHoursStore(
    (state) => state
  );
  const startDate = dayjs(selectedDate).startOf('month').toISOString();
  const endDate = dayjs(selectedDate)
    .startOf('month')
    .add(1, 'month')
    .toISOString();
  const menuItems: TabType[] = Object.values(BonusTypesEnums).map((type) => ({
    name: type,
    label: t(`bonusTypes.${type}`)
  }));
  const [currentMenuItem, setCurrentMenuItem] = useState<TabType | null>(null);
  const buhTabType: TabType = currentMenuItem ?? menuItems[0];
  const buhTypeSvc = buhTabType?.name;
  const columns = useGetColumns({ buhTypeSvc });

  // set current item before hook to silence TS for buhTypeSvc
  useEffect(() => {
    if (!currentMenuItem) {
      setCurrentMenuItem(buhTabType);
    }
  }, [currentMenuItem, buhTabType, setCurrentMenuItem]);

  const { data: EmployeeBonuses, isFetching } = useGetEmployeeBonuses(
    {
      employeeId,
      startDate,
      endDate,
      buhTypeSvc: buhTabType?.name,
      isEmployeePortal
    },
    {
      enabled: !!currentMenuItem && !!employeeId && !!startDate && !!endDate
    }
  );

  const allRows = useMemo(() => {
    if (!EmployeeBonuses?.periods) return [];

    const periodRows = EmployeeBonuses.periods.map((row) => ({
      ...row,
      id: `${row.dutyLineId}-${dayjs(row.startTime).format('HH:mm')}-${dayjs(
        row.endTime
      ).format('HH:mm')}`,
      rowType: 'data' as const
    }));

    const totalsRow = {
      id: 'totals-row',
      rowType: 'totals' as const,
      weekNumber: t('totals'),
      ...EmployeeBonuses.totals
    };

    return [...periodRows, totalsRow];
  }, [EmployeeBonuses, t]);

  const { mutateAsync: calculateDutyLinesForEmployee, isError } =
    useRecalculateDutyLinesForEmployeeMutation(
      isEmployeePortal ? 'employeeBonusesPortal' : 'employeeBonusesCard'
    );

  const onBonusTypeChange = (newTab: TabType) => {
    setCurrentMenuItem(newTab);
  };

  const onSingleAction = async () => {
    if (!startDate || !endDate || isError) return;

    await calculateDutyLinesForEmployee({
      startDate: dayjs(startDate).toDate(),
      endDate: dayjs(endDate).toDate(),
      employeeId
    });
  };

  useEffect(() => {
    if (!currentMenuItem) {
      setCurrentMenuItem(menuItems[0]);
    }
  }, [menuItems, setCurrentMenuItem, currentMenuItem]);

  if (!currentMenuItem) return null;

  return (
    <>
      <Header
        onSelectedDate={setSelectedDate}
        selectedDate={dayjs(selectedDate).toDate()}
        showSwitcherComponent
        showSingleAction
        onSingleAction={onSingleAction}
        showCustomMenuItemLabel
        singleActionButtonLabel={t('calculateBonuses')}
        switcherComponentData={{
          tabbedElement: currentMenuItem,
          tabulatedItems: menuItems,
          setTabbedElement: onBonusTypeChange,
          disableRouting: true
        }}
      />

      <TableCRUDLayout
        loading={isFetching}
        columns={columns}
        rows={allRows || []}
        getRowId={(row) => row.id}
        getRowClassName={(params) =>
          params.row.rowType === 'totals' ? 'totals-row' : ''
        }
        editable={false}
        dynamicHeight
      />
    </>
  );
};

export default EmployeeBonuses;
